/**
 * THE OPEN CHECKS, ON THE DEVICE.
 *
 * The demo banner says: cut the wifi, keep selling, reload, restore. The first
 * three worked. The fourth did not — `restoreOpenTables` reads the floor from
 * the SERVER, so a till reloaded during an outage came back with an empty floor
 * plan and four tables of people sitting in front of it with no bill. The
 * events were not lost; they were in the outbox, waiting. But the outbox is a
 * delivery queue, not a read model: it holds what has not gone yet and forgets
 * everything that has, so a check rung at seven and partly drained at eight
 * could not be reconstructed from it at nine.
 *
 * So the till keeps its own journal of the order events IT emitted, and folds
 * them on boot. It is the same shape as the drawer journal beside it: a local
 * read model that is never sent, in the same OPFS database, with the same
 * durability settings and nothing second to open.
 *
 * WHAT IT IS NOT. It is not a second source of truth. Once the network is back
 * the server's copy is adopted over the top — it is authoritative, and it knows
 * things this device cannot, like the round a guest ordered from their phone
 * while this till was offline. The journal's job ends the moment the server can
 * answer.
 *
 * PRUNED ON CLOSE, and the order of operations is the whole of it: the close is
 * APPENDED, and only then are the order's rows deleted. A crash in between
 * leaves an order in the journal whose fold is closed, which the restore skips
 * — the safe outcome. Deleting first and crashing would leave an open check on
 * the floor for a table that had paid and gone.
 */

import type { SqliteDriver } from "@gf/kernel/sqlite";
import { decodePayload, encodePayload } from "@gf/kernel/json";
import type { OutboundEvent } from "@gf/sync-client";

interface Row {
  readonly order_id: string;
  readonly table_ref: string | null;
  readonly event_json: string;
}

/** One order's events, in the order the till emitted them. */
export interface JournalledOrder {
  readonly orderId: string;
  readonly tableRef: string | null;
  readonly events: readonly OutboundEvent[];
}

export class SqliteOrderJournal {
  readonly #driver: SqliteDriver;

  constructor(driver: SqliteDriver) {
    this.#driver = driver;
  }

  /** Idempotent: every app start runs it. */
  async migrate(): Promise<void> {
    await this.#driver.exec(`
      CREATE TABLE IF NOT EXISTS order_journal (
        event_id   TEXT    NOT NULL PRIMARY KEY,
        order_id   TEXT    NOT NULL,
        table_ref  TEXT,
        seq        INTEGER NOT NULL,
        event_json TEXT    NOT NULL
      )
    `);
    // Restore reads by order, and pruning deletes by order. Without this every
    // boot scans the whole journal once per open table.
    await this.#driver.exec(
      `CREATE INDEX IF NOT EXISTS order_journal_by_order ON order_journal (order_id, seq)`,
    );
  }

  /**
   * Record an event this till emitted.
   *
   * `INSERT OR IGNORE` on the event id, because an event is emitted once but a
   * retry path that re-enqueued it must not produce a second copy in the fold.
   *
   * `seq` is a monotonic counter over the journal rather than a clock: two
   * events in the same millisecond are ordinary on a till, and ordering a fold
   * by a timestamp that ties is ordering it by nothing.
   */
  async append(event: OutboundEvent): Promise<void> {
    /**
     * THE TABLE COMES OUT OF THE EVENT, not out of a parameter.
     *
     * `order.opened` carries the `tableRef` that established the order, so the
     * journal is self-describing and nothing has to thread the table down
     * through the session factory — which does not know it, because a session
     * is created before the book files it under a table.
     */
    const payload = event.payload as { tableRef?: unknown } | null;
    const tableRef =
      event.type === "order.opened" && typeof payload?.tableRef === "string"
        ? payload.tableRef
        : null;

    await this.#driver.run(
      `INSERT OR IGNORE INTO order_journal (event_id, order_id, table_ref, seq, event_json)
       VALUES (?, ?, ?, (SELECT COALESCE(MAX(seq), 0) + 1 FROM order_journal), ?)`,
      // The tagged codec: every price on a check is a bigint, and
      // `JSON.stringify` throws on one outright.
      [event.eventId, event.streamId, tableRef, encodePayload(event)],
    );
  }

  /**
   * Every order the journal still holds, oldest event first.
   *
   * Returns ALL of them, closed ones included, because the caller folds and is
   * the only thing that can tell the difference — a journal that decided for
   * itself would need the aggregate, and the aggregate lives a layer up.
   */
  async orders(): Promise<readonly JournalledOrder[]> {
    const rows = await this.#driver.all<Row>(
      `SELECT order_id, table_ref, event_json FROM order_journal ORDER BY seq`,
      [],
    );

    const byOrder = new Map<string, { tableRef: string | null; events: OutboundEvent[] }>();
    for (const row of rows) {
      let entry = byOrder.get(row.order_id);
      if (!entry) {
        entry = { tableRef: row.table_ref, events: [] };
        byOrder.set(row.order_id, entry);
      } else if (entry.tableRef === null && row.table_ref !== null) {
        // Only the opener carries the table. If the journal ever holds an
        // order's later events without its opener — a partially pruned order,
        // or a crash mid-write — the table is whatever row has one.
        entry = { ...entry, tableRef: row.table_ref };
        byOrder.set(row.order_id, entry);
      }
      try {
        entry.events.push(decodePayload<OutboundEvent>(row.event_json));
      } catch {
        // Half-written by a crash mid-transaction. Skipping the row is right:
        // an order missing one event folds to something the restore can still
        // show, and refusing the whole journal over it would lose the floor.
        continue;
      }
    }

    return [...byOrder.entries()].map(([orderId, entry]) => ({
      orderId,
      tableRef: entry.tableRef,
      events: entry.events,
    }));
  }

  /** Forget a settled order. Called after its close has been appended. */
  async forget(orderId: string): Promise<void> {
    await this.#driver.run(`DELETE FROM order_journal WHERE order_id = ?`, [orderId]);
  }
}
